import type { CSSProperties } from 'react'
import type { AllianceTaskOptionId } from './allianceApi'

/**
 * Shared fantasy-title style for the alliance-task modals: bold classical Roman serif
 * (Cinzel / Trajan), all-caps, warm antique-gold vertical gradient with a light-gold top
 * highlight, a thick dark-brown outer stroke (painted behind the fill), and a soft
 * brown/black drop shadow for a beveled, embossed look. Size in `cqw` is set per modal.
 */
export const ALLIANCE_TASK_TITLE_STYLE: CSSProperties = {
  fontFamily: '"Cinzel", "Trajan Pro", "Times New Roman", serif',
  fontWeight: 800,
  textTransform: 'uppercase',
  letterSpacing: '0.015em',
  lineHeight: 1.05,
  margin: 0,
  whiteSpace: 'nowrap', // always one line
  // bright warm antique-gold vertical gradient (light-gold highlight at the top)
  backgroundImage: 'linear-gradient(180deg, #fff4c8 0%, #f4d472 38%, #dca644 70%, #b07e2c 100%)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  color: 'transparent',
  // thin dark-brown outer stroke behind the gold fill (keeps the gold clean)
  WebkitTextStroke: '0.7px #402611',
  paintOrder: 'stroke',
  // dark bevel edge + soft brown/black drop shadow
  textShadow: '0 1px 0 rgba(52,30,10,0.6), 0 2px 4px rgba(14,7,3,0.55)',
}

/** Canonical order of the 5 fixed strategic options (matches backend ALLIANCE_TASK_OPTIONS). */
export const ALLIANCE_TASK_OPTION_ORDER: AllianceTaskOptionId[] = [
  'aps',
  'conquer',
  'raid',
  'diplomacy',
  'technology',
]

export interface AllianceTaskOptionMeta {
  /** i18n key with English fallback. */
  i18nKey: string
  fallback: string
  descKey: string
  descFallback: string
  icon: string
  accent: string
}

export const ALLIANCE_TASK_OPTION_META: Record<AllianceTaskOptionId, AllianceTaskOptionMeta> = {
  aps: {
    i18nKey: 'alliance.taskOptionAps',
    fallback: 'Increase APS',
    descKey: 'alliance.taskOptionApsDesc',
    descFallback: 'Grow the alliance power score (sum of members’ KPS).',
    icon: '📈',
    accent: '#c9a24b',
  },
  conquer: {
    i18nKey: 'alliance.taskOptionConquer',
    fallback: 'Conquer & Recruit',
    descKey: 'alliance.taskOptionConquerDesc',
    descFallback: 'Take enemy castles and grow the roster.',
    icon: '⚔️',
    accent: '#b5482f',
  },
  raid: {
    i18nKey: 'alliance.taskOptionRaid',
    fallback: 'Raid for Resources',
    descKey: 'alliance.taskOptionRaidDesc',
    descFallback: 'Plunder resources from rival castles.',
    icon: '💰',
    accent: '#b6892f',
  },
  diplomacy: {
    i18nKey: 'alliance.taskOptionDiplomacy',
    fallback: 'Diplomacy',
    descKey: 'alliance.taskOptionDiplomacyDesc',
    descFallback: 'Forge pacts and exchange gifts with other alliances.',
    icon: '🤝',
    accent: '#3f7a52',
  },
  technology: {
    i18nKey: 'alliance.taskOptionTechnology',
    fallback: 'Technology',
    descKey: 'alliance.taskOptionTechnologyDesc',
    descFallback: 'Advance the alliance technology tree.',
    icon: '⚗️',
    accent: '#3f6a8a',
  },
}

export interface RankStyle {
  bg: string
  border: string
  text: string
  /** Governance tier this rank maps to (top #1, mid #2–3, low #4–5). */
  tier: 'top' | 'mid' | 'low'
}

/** Color a ranked option by its governance tier (rank is 0-based; 0 = most-voted). */
export function allianceTaskRankStyle(rank: number): RankStyle {
  if (rank <= 0) {
    return { bg: 'rgba(63,122,82,0.18)', border: '#3f9a5c', text: '#7be0a0', tier: 'top' }
  }
  if (rank <= 2) {
    return { bg: 'rgba(63,106,138,0.16)', border: '#5a86aa', text: '#9fd0ff', tier: 'mid' }
  }
  return { bg: 'rgba(150,60,45,0.16)', border: '#a5533a', text: '#e6a58f', tier: 'low' }
}

/** Governance delta preview for picking a given rank (matches backend +5 / 0 / −5). */
export function allianceTaskGovernanceDelta(rank: number): number {
  if (rank <= 0) return 5
  if (rank <= 2) return 0
  return -5
}

/** APS growth targets (only when the official option is `aps`): 10% → +5, 20% → +10, 30% → +20. */
export const ALLIANCE_TASK_APS_TARGETS: { percent: number; governance: number }[] = [
  { percent: 10, governance: 5 },
  { percent: 20, governance: 10 },
  { percent: 30, governance: 20 },
]

/** Attack-plan castle counts per option — Conquer (recruit) vs Raid (loot). */
export const ALLIANCE_TASK_ATTACK_COUNTS: Record<'conquer' | 'raid', number[]> = {
  conquer: [2, 4, 6],
  raid: [5, 10, 15],
}

/** Voting-modal artwork per option (see public/assets/alliance-task/voting-modal). */
export const ALLIANCE_TASK_VOTE_ICON: Record<AllianceTaskOptionId, string> = {
  aps: 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/redesign/alliance/alliance-task/voting-modal/aps-icon.webp',
  conquer: 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/redesign/alliance/alliance-task/voting-modal/conquer-icon.webp',
  raid: 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/redesign/alliance/alliance-task/voting-modal/raid-icon.webp',
  diplomacy: 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/redesign/alliance/alliance-task/voting-modal/diplomacy-icon.webp',
  technology: 'https://s0swaypl0zqnzq8t.public.blob.vercel-storage.com/redesign/alliance/alliance-task/voting-modal/tech-icon.webp',
}

/** Diplomacy levels (legacy — old single-level task). Unused by the new alliance-count flow. */
export const ALLIANCE_TASK_DIPLOMACY_LEVELS: { level: number; governance: number }[] = [
  { level: 1, governance: 5 },
  { level: 2, governance: 10 },
  { level: 3, governance: 20 },
]

/** Diplomacy plan: raise the relationship tier with 1 / 2 / 3 alliances (mirrors Conquer counts). */
export const ALLIANCE_TASK_DIPLOMACY_COUNTS: number[] = [1, 2, 3]

/** Relationship tiers (ascending); `spirit` is the implicit floor a leader can't target. */
export const ALLIANCE_DIPLOMACY_LEVEL_ORDER = ['spirit', 'ceasefire', 'alliance', 'marriage'] as const
export type AllianceDiplomacyLevel = (typeof ALLIANCE_DIPLOMACY_LEVEL_ORDER)[number]

/** Target tiers a leader may pick per alliance (with i18n key + English fallback label). */
export const ALLIANCE_TASK_DIPLOMACY_TARGET_LEVELS: {
  level: 'ceasefire' | 'alliance' | 'marriage'
  i18nKey: string
  label: string
}[] = [
  { level: 'ceasefire', i18nKey: 'alliance.relationTierCeasefire', label: 'Ceasefire' },
  { level: 'alliance', i18nKey: 'alliance.relationTierAlliance', label: 'Alliance' },
  { level: 'marriage', i18nKey: 'alliance.relationTierMarriage', label: 'Marriage' },
]

/** Ordinal rank of a relationship tier (spirit=0 … marriage=3); -1 if unknown. */
export function allianceDiplomacyLevelRank(level: string | null | undefined): number {
  return ALLIANCE_DIPLOMACY_LEVEL_ORDER.indexOf(String(level || 'spirit') as AllianceDiplomacyLevel)
}
